import React, { Component } from 'react';
import Header from '../../.././Header';
import Image from '../../.././Image';
import Linkify from 'react-linkify';
import classnames from 'classnames';

export default class Source extends Component {
  // static propTypes = {}
  // static defaultProps = {}
  // state = {}

  render() {
    const { className } = this.props;

    const navLinks = [
      {path: "/projects", title: "Projects"},
      {path: "/projects/angellist", title: "AngelList"}
    ]

    const slug = "angellist/source";
    const filter_screenshots = [
      {id: 'filter-0', title: 'Signal Interaction', content: 'Mocks for interacting with a signal. Toggling enabled the options of each signal to be hidden until the recruiter wanted to interact with it. Reduced clutter in the interface given there were different filter types, e.g. checkbox, sliders, etc.'},
      {id: 'filter-1', title: 'Search and Facets', content: 'More detailed exploration of interactions within an expanded signal. Supported multiple selections, list expansion and search. Counts indicate the number of candidates within a facet.'},
    ];
    const layout_screenshots = [
      {id: 'layout-0', title: 'Initial Layout', content: 'Started with a left sidebar for the signals. This was not ideal as it pushed the main column of candidates to the far right, resulting in the core actions being close to the edge of the screen. '},
      {id: 'layout-1', title: 'Final Layout', content: 'Moved the signals to the right instead to enable the candidate list to be in the middle. The signals were not regularly interacted with, so having them at the side made sense. Adopted a card style for candidates to further distinguish the different sections of the page.'},
      {id: 'layout-2', title: 'Candidate Modal', content: 'Clicking on a candidate in Source toggles this modal which shows more information about the candidate. This reduces the need to navigate to the candidate\'s profile page. The modal also shares the same action bar so recruiters can easily reach out, bookmark or share the candidate.'},
      {id: 'layout-3', title: 'Saved Search', content: 'Recruiters can save a set of signals as a Saved Search, and other team members as subscribers. Candidates who match this search are sent out in a weekly digest mailer.'},
    ];
    const upgrade_screenshots = [
      {id: 'upgrade-0', title: 'Initial Upgrade Modal', content: 'Source subscriptions were priced by company size, and we wanted a way to show the different plans and price points. However, recruiters were confused with this interface and thought they could choose the cheaper plan, which led to us having to manually upgrade their subscription.'},
      {id: 'upgrade-1', title: 'Final Upgrade Modal', content: 'Iterated on the pricing plans interface to more clearly highlight the decision the recruiter had to make. This reduced confusion significantly and the operations support required to upgrade plans. This interface also enabled us to indicate the availability of annual subscriptions.'},
    ];
    const intro_screenshots = [
      {id: 'intro-0', title: 'Introduction Modal', content: 'A simple modal to introduce the changes and communicate the value proposition of Source to new customers. Designed the assets to be bright and delightful.'},
      {id: 'intro-1', title: 'Landing Page', content: 'The landing page explains the product and benefits to customers, and goes into detail about the pricing plans and available signals. Currently viewable at https://angel.co/source.'},
    ];
    const misc_screenshots = [
      {id: 'misc-0', title: 'Design Assets', content: 'Graphical assets used on the landing page to describe the product\'s features. I decided to use an abstract style than screenshots to give us flexibility in making changes to the user interface.'},
      {id: 'misc-1', title: 'Source Digest', content: 'Recruiters receive this weekly mailer containing new candidates who match their search. I adopted a clean yet informative style to enable recruiters to easily focus on the content.'},
    ];

    return (
      <div className={classnames('Source', className)}>
        <Header links={navLinks} />
        <div className="container">
          <div className="section">
            <div className="section-content">
              <div className="section-header">Source</div>
              <div className="section-title">Description</div>
              <div className="section-copy">
                <ul>
                  <li><strong>Objective</strong>: Launching a subscription service for sourcing candidates</li>
                  <li><strong>Role</strong>: Product manager and designer</li>
                  <li><strong>Team</strong>: Cagri Aksay, Tiffany Wu, Adam Shi</li>
                  <li><strong>Duration</strong>: Jun 2017—Dec 2017 (7 months)</li>
                </ul>
              </div>
              <div className="section-title">Highlights</div>
              <div className="section-copy">
                <ul>
                  <li>Designed, prototyped, tested and launched in 4 months</li>
                  <li>Achieved $40K MRR in 3 months after 50% rollout</li>
                  <li>Attained 53% trial activation and 62% retention rates</li>
                  <li>Increased candidate search activity per recruiter by 55%</li>
                  <li>Increased engaged candidate matches per recruiter by 8%</li>
                  <li>Ran experiments to validate subscription prices</li>
                </ul>
              </div>
              <div className="section-title">Summary</div>
              <div className="section-copy">
                <p>
                  Source is a candidate search tool that gives founders and recruiters access to over 20 signals for sourcing, many unique to the AngelList platform. I worked on Source with three engineers—Cagri Aksay, Tiffany Wu and Adam Shi. The old version of candidate search hadn't been meaningfully improved over the past 3 years, so we decided to rebuild the product from the ground up with Algolia and React. It was also the first time we were monetizing candidate search on top of our talent marketplace, and the results so far seem promising. 
                </p>
              </div>
              <div className="section-title">Problem</div>
              <div className="section-copy">
                <p>
                  Noise is one of the biggest issues that startups face when it comes to sourcing candidates. Not just knowing which candidates are high-quality and a good fit, but if they're actively looking and ready to move as well. The old version of candidate search had a limited set of filters that made it difficult to separate the signal from noise.
                </p>
                <p>
                  From a business standpoint, our talent marketplace had been free to use these last 5 years, and it was time to develop a sustainable and scalable monetization strategy for the core platform.
                </p>
              </div>
              <div className="section-title">Approach</div>
              <div className="section-copy">
                <p>
                  <strong>Research:</strong> We talked to founders and recruiters to understand the kind of signals they look for in candidates, and also looked at data on how they'd been using our product. We learned that being able to filter by profile attributes wasn't sufficient, and that understanding where a candidate was in their job search was important as well.
                </p>
                <p>
                  With our talent marketplace, we had proprietary information about a candidate's behavior—such as how recently active or how responsive they were. We could know if a candidate had just started their search, or if they were passively looking. This key differentiator struck a chord with the startups we talked to, and many were willing to pay for access to the additional signals.
                </p>
                <p>
                  <strong>Prototyping:</strong> The team brainstormed all the different signals that we thought would be useful and differentiable, then prioritized and pared down the list to the most impactful ones to start with. We then built a rough prototype that handled the basic filtering and faceting of these signals as a way to test the product internally.
                </p>
              </div>
              <div className="section-images">
                {
                  Object.keys(filter_screenshots).map(function(key) {
                    return (
                      <Image slug={slug} id={filter_screenshots[key]['id']} title={filter_screenshots[key]['title']} content={filter_screenshots[key]['content']} />
                    )
                  })
                }
              </div>
              <div className="section-copy">
                <p>
                  We built out the frontend by designing and developing reusable view components with the idea of extending them to the rest of the product eventually. We iterated on the page layout, and built out key features like the candidate modal and saved searches.
                </p>
              </div>
              <div className="section-images">
                {
                  Object.keys(layout_screenshots).map(function(key) {
                    return (
                      <Image slug={slug} id={layout_screenshots[key]['id']} title={layout_screenshots[key]['title']} content={layout_screenshots[key]['content']} />
                    )
                  })
                }
              </div>
              <div className="section-copy">
                <p>
                  <strong>Pricing:</strong> We studied competitors and similar tools, and opted to price our product as a subscription service. As our customers varied from founders to recruiting teams, we decided to price the subscription based on company size, rather than per seat. Each subscription would also come with a 14-day trial so customers could try out the product first.
                </p>
                <p>
                  We wanted our pricing to reflect the value the product would deliver to the customer, and given that a larger team would be able to use the same product to recruit for many positions, this pricing seemed justified. We also had a free plan that came with a limited set of signals in order to support more price-sensitive teams.
                </p>
                <p>
                  Throughout the rollout, we experimented with increased (1.5x) and reduced (0.5x) prices in order to understand the effects on our conversion and activation rates. Increased prices yielded about 50% decrease in both conversion and activation rates (0.75x relative revenue), while reduced prices yield around 60% increase in conversion and neutral change in activation rates (0.8x relative revenue). Thus, we decided to maintain our initial prices.
                </p>
              </div>
              <div className="section-images">
                {
                  Object.keys(upgrade_screenshots).map(function(key) {
                    return (
                      <Image slug={slug} id={upgrade_screenshots[key]['id']} title={upgrade_screenshots[key]['title']} content={upgrade_screenshots[key]['content']} />
                    )
                  })
                }
              </div>
              <div className="section-copy">
                <p>
                  <strong>Rollout:</strong> We released an MVP after 3 months to a few select startups with the objective of gathering feedback and improving the user experience. When the product was more polished, we rolled out an experiment to 15% of startups and measured metrics around recruiter usage (candidate search activity) and recruiter success (engaged candidate matches). As we were replacing the old version of candidate search, we maintained feature parity and integrated it thoughtfully to minimize switching costs. We ran the experiment for around 6 weeks to improve data quality and negate novelty effects.
                </p>
                <p>
                  During this period, we talked to customers for feedback and built out tools and processes to automate our operations. We also set up dashboards to monitor our monetization metrics and experiment results. When we were confident that Source was well-received by customers and had a positive impact on our metrics, we slowly ramped it up to more startups over time. In Jan 2018, we launched Source to 100%, and the product is currently generating $40K MRR.
                </p>
              </div>
              <div className="section-images">
                {
                  Object.keys(intro_screenshots).map(function(key) {
                    return (
                      <Image slug={slug} id={intro_screenshots[key]['id']} title={intro_screenshots[key]['title']} content={intro_screenshots[key]['content']} />
                    )
                  })
                }
              </div>
              <div className="section-title">Role</div>
              <div className="section-copy">
                <p>
                  My role in the project was as product manager and designer. I worked with the engineers to plan the product roadmap and managed the project's sprints on a weekly basis. I identified monetization and usage metrics that we tracked throughout the project's lifecycle, and occasionally wrote some MySQL to improve our dashboards. I participated in user and competitor research, as well as conducted customer calls to collect feedback.
                </p>
                <p>
                  As the designer, I mocked up variations of the interface used for filtering signals and explored different page layouts. I also designed the emails and core flows of the product, such as creating and saving searches. I worked with engineers to implement and style the views.
                </p>
                <p>
                  Beyond product work, I handled all account management and sales operations as well. I responded to support tickets, took sales calls, documented customer success processes and developed marketing automations for interacting with customers.
                </p>
              </div>
              <div className="section-title">More Designs</div>
              <div className="section-images">
                {
                  Object.keys(misc_screenshots).map(function(key) {
                    return (
                      <Image slug={slug} id={misc_screenshots[key]['id']} title={misc_screenshots[key]['title']} content={misc_screenshots[key]['content']} />
                    )
                  })
                }
              </div>
              <div className="section-title">Tech / Tools</div>
              <div className="section-copy">
                <ul>
                  <li>Designed in Sketch</li>
                  <li>Developed the frontend in React, Rails, HAML and SASS</li>
                  <li>Built dashboards on Chartio with MySQL</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Projects/AngelList/Source/index.js